import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { toast } from "react-toastify";
import {
  buildStateListParams,
  deleteStateMaster,
  getCountriesForDropdown,
  getStateMasterById,
  getStatesMaster,
} from "../../api/superAdminApi";
import SuperAdminPageHeader from "./SuperAdminPageHeader";
import AddStateModal from "./StateMaster/AddStateModal";
import GeoMasterActionCell from "./GeoMasterActionCell";
import GeoMasterDeleteDialog from "./GeoMasterDeleteDialog";

const ROWS_PER_PAGE_OPTIONS = [10, 20, 25, 50];
const TABLE_HEADERS = ["S.No", "State", "Country", "Country code", "Actions"];

export default function VMSuperAdminStateMaster() {
  const [countries, setCountries] = useState([]);
  const [countryId, setCountryId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const filtersRef = useRef({ countryId, appliedSearch, page, rowsPerPage });
  filtersRef.current = { countryId, appliedSearch, page, rowsPerPage };

  const fetchList = useCallback(async (overrides = {}) => {
    const f = { ...filtersRef.current, ...overrides };
    if (!f.countryId) {
      setRows([]);
      setTotalRecords(0);
      return;
    }
    setLoading(true);
    try {
      const params = buildStateListParams({
        page: f.page,
        pageSize: f.rowsPerPage,
        countryId: f.countryId,
        search: f.appliedSearch,
      });
      const res = await getStatesMaster(params);
      const body = res.data;
      if (body?.status_code && body.status_code !== 200) {
        throw new Error(body.message || "Failed to load states");
      }
      setRows(Array.isArray(body?.data) ? body.data : []);
      setTotalRecords(Number(body?.totalCount) || 0);
      if (typeof body?.page === "number") setPage(body.page);
    } catch (error) {
      setRows([]);
      setTotalRecords(0);
      toast.error(error?.response?.data?.message || error?.message || "State master API unavailable.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCountries = useCallback(async () => {
    setLoadingCountries(true);
    try {
      const res = await getCountriesForDropdown();
      const list = Array.isArray(res.data?.data) ? res.data.data : [];
      setCountries(list);

      // Default select India when nothing is selected yet
      if (!filtersRef.current.countryId && list.length) {
        const india =
          list.find((c) => String(c.country_code || "").toUpperCase() === "IN") ||
          list.find((c) => String(c.country_name || "").toLowerCase() === "india");
        if (india?.cid != null) {
          const id = String(india.cid);
          setCountryId(id);
          fetchList({ countryId: id, page: 0, appliedSearch: "" });
        }
      }
    } catch {
      setCountries([]);
      toast.error("Failed to load countries.");
    } finally {
      setLoadingCountries(false);
    }
  }, [fetchList]);

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  const selectedCountry = countries.find((c) => String(c.cid) === String(countryId)) || null;

  const handleCountryChange = (value) => {
    setCountryId(value);
    setPage(0);
    setAppliedSearch("");
    setSearchInput("");
    fetchList({ countryId: value, appliedSearch: "", page: 0 });
  };

  const handleSearch = () => {
    if (!countryId) {
      toast.info("Select a country first.");
      return;
    }
    const nextSearch = searchInput.trim();
    setAppliedSearch(nextSearch);
    setPage(0);
    fetchList({ appliedSearch: nextSearch, page: 0 });
  };

  const handlePageChange = (_, newPage) => {
    setPage(newPage);
    fetchList({ page: newPage });
  };

  const handleRowsPerPageChange = (e) => {
    const next = parseInt(e.target.value, 10);
    setRowsPerPage(next);
    setPage(0);
    fetchList({ rowsPerPage: next, page: 0 });
  };

  const openAdd = () => {
    setEditingRow(null);
    setFormOpen(true);
  };

  const openEdit = async (row) => {
    if (!row?.id) return;
    setEditLoading(true);
    try {
      const res = await getStateMasterById(row.id);
      const body = res.data;
      if (body?.status_code === 200 && body?.data) {
        setEditingRow(body.data);
        setFormOpen(true);
      } else {
        toast.error(body?.message || "Failed to load state.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to load state.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await deleteStateMaster(deleteRow.id);
      const body = res.data;
      if (body?.status_code === 200) {
        const citiesDeleted = body?.data?.cities_deleted ?? 0;
        toast.success(body.message || `State deleted (${citiesDeleted} cities soft-deleted).`);
        setDeleteRow(null);
        fetchList();
      } else {
        toast.error(body?.message || "Failed to delete state.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete state.");
    } finally {
      setDeleting(false);
    }
  };

  const from = totalRecords === 0 ? 0 : page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, totalRecords);

  return (
    <Box>
      <SuperAdminPageHeader
        title="State Master"
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openAdd}
            disabled={!countries.length}
          >
            Add state
          </Button>
        }
      />

      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="state-master-country-label">Country</InputLabel>
          <Select
            labelId="state-master-country-label"
            label="Country"
            value={countryId}
            onChange={(e) => handleCountryChange(e.target.value)}
            disabled={loadingCountries}
          >
            {countries.map((c) => (
              <MenuItem key={c.cid} value={String(c.cid)}>
                {c.country_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label="Search state"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleSearch();
          }}
          disabled={!countryId}
          sx={{ minWidth: 220 }}
        />
        <Button variant="contained" onClick={handleSearch} disabled={loading || !countryId}>
          Search
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {!countryId
          ? "Select a country to view states."
          : `Total states (filtered): ${loading ? "…" : totalRecords.toLocaleString()}${
              !loading && totalRecords > 0 ? ` · Showing ${from} to ${to}` : ""
            }`}
        {editLoading ? " · Loading state…" : ""}
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "primary.main" }}>
              {TABLE_HEADERS.map((h) => (
                <TableCell key={h} sx={{ color: "primary.contrastText", fontWeight: 700 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!countryId ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Choose a country to load states.
                </TableCell>
              </TableRow>
            ) : rows.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No states found for the current criteria.
                </TableCell>
              </TableRow>
            ) : loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_HEADERS.length} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow key={row.id} hover>
                  <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{row.state || "—"}</TableCell>
                  <TableCell>{row.country_name || selectedCountry?.country_name || "—"}</TableCell>
                  <TableCell>{row.country_code || "—"}</TableCell>
                  <TableCell>
                    <GeoMasterActionCell
                      onEdit={() => openEdit(row)}
                      onDelete={() => setDeleteRow(row)}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                count={totalRecords}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>

      <AddStateModal
        open={formOpen}
        initialData={editingRow}
        defaultCountry={selectedCountry}
        onClose={() => {
          setFormOpen(false);
          setEditingRow(null);
        }}
        onSuccess={() => {
          if (countryId) fetchList();
          loadCountries();
        }}
      />

      <GeoMasterDeleteDialog
        open={Boolean(deleteRow)}
        title="Delete state"
        message={`Delete "${deleteRow?.state || "this state"}"? Related cities will also be soft-deleted and hidden from lists.`}
        loading={deleting}
        onClose={() => !deleting && setDeleteRow(null)}
        onConfirm={handleDelete}
      />
    </Box>
  );
}
